import "./instrument";
import { createRoot } from "react-dom/client";
import "./main.css";
import "./maintenance.css";
import "./i18n";
import * as Sentry from "@sentry/react";
import { MaintenancePage } from "./components/maintenance/MaintenancePage";
import { getMaintenanceConfig } from "./config/maintenance";
import jssVariables from "./jss_setup";
import { getFooterBackground } from "./jss_setup/colors";
import { applyThemeCssVars } from "./utils/themeCssVars";

// In development, unregister all service workers immediately to prevent HMR conflicts
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

applyThemeCssVars({
  colors: {
    ...jssVariables.colors,
    footer_background: getFooterBackground(),
  },
  spacing: {
    half_gap: jssVariables.half_gap,
    normal_gap: jssVariables.normal_gap,
    double_gap: jssVariables.double_gap,
    center_padding_large: jssVariables.spacing.center_padding_large[1],
  },
  layout: {
    navbar_height: jssVariables.navbar_height,
    card_img_height_mobile: jssVariables.card_img_height_mobile,
    card_img_height_desktop: jssVariables.card_img_height_desktop,
    card_img_width_desktop: jssVariables.card_img_width_desktop,
  },
  shadows: {
    smallShadow: jssVariables.shadows.smallShadow,
    normalShadow: jssVariables.shadows.normalShadow,
    hoverShadow: jssVariables.shadows.hoverShadow,
  },
  border_radius: jssVariables.border_radius,
  typography: {
    fontFamily: jssVariables.fontFamily,
  },
});

const maintenance = getMaintenanceConfig();
const root = createRoot(document.getElementById("root") as HTMLElement);

async function renderNormalApp() {
  const [
    { Auth0Provider },
    { QueryClientProvider },
    { Provider },
    { default: App },
    { default: store },
    { queryClient },
  ] = await Promise.all([
    import("@auth0/auth0-react"),
    import("@tanstack/react-query"),
    import("react-redux"),
    import("./App"),
    import("./store"),
    import("./queryClient"),
  ]);

  const withAuth = import.meta.env.VITE_APP_PRIVATE_WEBSITE === "true";
  const brand = import.meta.env.VITE_APP_BRANDNAME || "";

  if (brand === "travily") {
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]) {
      (window as any).dataLayer.push(args);
    }
    gtag("js", new Date());
    gtag("config", "G-NL3JB7CNYV");

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-NL3JB7CNYV";
    document.head.appendChild(script);
  }

  const auth0ProviderProps = {};

  root.render(
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        {withAuth ? (
          <Sentry.ErrorBoundary fallback={<div>Error</div>}>
            <Auth0Provider
              domain={import.meta.env.VITE_APP_AUTH0_DOMAIN}
              clientId={import.meta.env.VITE_APP_AUTH0_CLIENTID}
              useRefreshTokens={true}
              authorizationParams={{
                redirect_uri: `${window.location.origin}/authnavigator`,
                logo: import.meta.env.VITE_APP_LOGO || "",
                background_img: import.meta.env.VITE_APP_AUTH0_BACKGROUND || "",
                base_color: jssVariables.colors.base_color,
                title: import.meta.env.VITE_APP_AUTH0_TITLE || "",
              }}
              {...auth0ProviderProps}
            >
              <App />
            </Auth0Provider>
          </Sentry.ErrorBoundary>
        ) : (
          <Sentry.ErrorBoundary fallback={<div>Error</div>}>
            <App />
          </Sentry.ErrorBoundary>
        )}
      </Provider>
    </QueryClientProvider>,
  );
}

if (maintenance.enabled) {
  root.render(
    <Sentry.ErrorBoundary fallback={<div>Error</div>}>
      <MaintenancePage config={maintenance} />
    </Sentry.ErrorBoundary>,
  );
} else {
  void renderNormalApp();
}
